import { ElementType, FC, useContext, useMemo, useState } from 'react';
import { BsShieldExclamation } from 'react-icons/bs';
import {
  CgCheckO,
  CgCloseO,
  CgMail,
  CgMathPlus,
  CgMoreVerticalAlt,
  CgSoftwareDownload,
  CgTrash,
} from 'react-icons/cg';
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Link,
  Menu,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ColumnDef, Row } from '@tanstack/react-table';

import { buildIncludeParams } from '~/core/shared/api';
import {
  useAnalyseAttachmentForFraud,
  useApplicationDocs,
  useApplicationUncategorizedDocs,
  useDeleteApplicationDoc,
  useUpdateApplicationDoc,
} from '~/core/shared/api/middle-office/application';
import { Select } from '~/core/shared/components';
import { DateTimeDisplay } from '~/core/shared/components/DateTimeDisplay';
import {
  AttachmentUploadState,
  CategoryParent,
  DocStatusEnum,
  EMPTY_ARR,
  EMPTY_OBJ,
  FeatureKey,
} from '~/core/shared/constants';
import { useFeature } from '~/core/shared/contexts/features';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useTableParams, useUpToDateFileAttribute } from '~/core/shared/hooks';
import { useCan } from '~/core/shared/hooks/useCan';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Tooltip } from '~/core/shared/lib/chakraUi/components';
import { Checkbox } from '~/core/shared/lib/chakraUi/components/Checkbox';
import {
  Doc,
  Feature,
  FraudAnalysisUpdate,
  PaginationParams,
  Sorting,
  SortParams,
} from '~/core/shared/types';
import { isDeniedHook } from '~/core/shared/utils/general';
import { createFilterParams } from '~/core/shared/utils/queries';
import {
  useSubscribeToDocumentAttachmentUploadStateChanged,
  useSubscribeToFraudAnalysisUpdated,
} from '~/core/shared/websocketApi';
import { ApplicationContext, DocsTabContext } from '../application';
import { DocsTabContextType } from '../application/tabs/Docs.component';
import { ReactQueryTable } from '../table';
import { FraudActionsMenuItems } from './FraudActionsMenuItems';
import { FraudRiskPill } from './FraudRiskPill';
import { RedactionStatusPill } from './RedactionStatusPill';

type DocsTabTypeTableProps = {
  type: string;
  perPage?: number;
  includes?: string[];
  documentTypeCategory?: string;
};

type UploadStatuses = Record<string, AttachmentUploadState>;

const calculateMarkAsOptions = (
  currentStatus: DocStatusEnum,
): DocStatusEnum[] =>
  Object.values(DocStatusEnum).filter((option) => option !== currentStatus);

const getStatusIcon = (status: DocStatusEnum): ElementType | undefined => {
  switch (status) {
    case DocStatusEnum.Checked:
      return CgCheckO;

    case DocStatusEnum.Rejected:
      return CgCloseO;

    default:
      return undefined;
  }
};

const getStatusColour = (status: DocStatusEnum): string => {
  switch (status) {
    case DocStatusEnum.Checked:
      return 'green.700';

    case DocStatusEnum.Rejected:
      return 'red.600';

    default:
      return 'gray.600';
  }
};

// TODO: this is SO close to the DocumentDownloadButton component in the supporting documents, we need to find a way to reuse this logic instead of duplicating
const DownloadDisplay = ({
  uploadStatuses,
  fraudUpdates,
  row,
  enabled,
}: {
  uploadStatuses: UploadStatuses;
  fraudUpdates: FraudAnalysisUpdate[];
  row: Row<Doc>;
  enabled: boolean;
}) => {
  const fileUploadStatus = uploadStatuses[row.original.file!.id];

  const file = useUpToDateFileAttribute(row.original.file, fraudUpdates);

  // Default to ready when the backend didn't provide the flag (feature off).
  const downloadReady = file?.downloadReady ?? true;

  const status = useMemo(() => {
    if (enabled) {
      if (row.original.file!.uploadState === AttachmentUploadState.Uploading) {
        if (fileUploadStatus === AttachmentUploadState.Quarantined) {
          return AttachmentUploadState.Quarantined;
        }
        if (fileUploadStatus === AttachmentUploadState.Uploaded) {
          return AttachmentUploadState.Uploaded;
        }
        return AttachmentUploadState.Uploading;
      }
      return row.original.file?.uploadState;
    }
    return AttachmentUploadState.Uploaded;
  }, [enabled, fileUploadStatus, row.original.file!.uploadState]);

  switch (status) {
    case AttachmentUploadState.Uploading:
      return (
        <Tooltip content="Please wait, we are uploading and scanning your document.">
          <span>
            <Button
              colorPalette="gray"
              fontWeight="normal"
              loading
              loadingText="Uploading"
              ml={4}
              rounded="lg"
              variant="ghost"
            >
              Uploading
            </Button>
          </span>
        </Tooltip>
      );
    case AttachmentUploadState.Quarantined:
      return (
        <Tooltip content="This file may contain malicous data and has been quarantined for your protection.">
          <span>
            <Button
              colorPalette="red"
              disabled
              fontWeight="normal"
              ml={4}
              rounded="lg"
              variant="ghost"
            >
              <BsShieldExclamation />
              Quarantined
            </Button>
          </span>
        </Tooltip>
      );
    default:
      if (!downloadReady) {
        return (
          <Tooltip content="Please wait, we are still scanning this document.">
            <span>
              <Button
                colorPalette="gray"
                disabled
                fontWeight="normal"
                loading
                loadingText="Scanning"
                ml={4}
                rounded="lg"
                variant="ghost"
              >
                Scanning
              </Button>
            </span>
          </Tooltip>
        );
      }

      return (
        <IconButton
          aria-label="download"
          asChild
          colorPalette="gray"
          ml={4}
          rounded="lg"
          variant="ghost"
        >
          <Link href={row.original.file?.url} target="_blank">
            <CgSoftwareDownload />
          </Link>
        </IconButton>
      );
  }
};

const isRejected = (status: string): boolean =>
  status === DocStatusEnum.Rejected;

const isRejectedEmailSent = (rejectedEmailSend: boolean | undefined): boolean =>
  rejectedEmailSend === true;

// @TODO: this needs to be refactored to not access tenant-specific definitions
const appendDisplayNameToDoc = (doc: Doc): Doc => {
  let ownerName;

  if (doc.individual) {
    ownerName = doc.individual.docsName;

    if (doc.individual.role) {
      ownerName += ` - ${doc.individual.role}`;
    } else {
      ownerName += ` - ${doc.individual.definition}`;
    }
  }

  if (doc.applicant) {
    ownerName = `${doc.applicant.docsName} - Applicant`;
  }

  if (doc.business) {
    ownerName = doc.business.docsName;
  }

  if (doc.asset) {
    ownerName = doc.asset.docsName;
  }

  return {
    ...doc,
    displayName: [
      [doc.type, ownerName, doc.name].filter(Boolean).join(' - '),
      doc.documentRequirements ? `: ${doc.documentRequirements}` : '',
    ]
      .filter(Boolean)
      .join(''),
  };
};

const getColumns = (
  selectedDocs: Doc[],
  onSelectChange: (checked: boolean, doc: Doc) => void,
  docTypes: DocsTabContextType['docTypes'],
  can: (permission: string) => boolean,
  canAny: (permissions: readonly string[]) => boolean,
  updateType: (docId: string, documentType: string) => Promise<void>,
  abortDownloadingAttachmentsFlag: Feature,
  uploadStatuses: UploadStatuses,
  fraudUpdates: FraudAnalysisUpdate[],
  deleteDoc: (docId: string) => Promise<void>,
  setDocNeedsToBeRejected: DocsTabContextType['setDocNeedsToBeRejected'],
  docRejectedModalApi: DocsTabContextType['docRejectedModalApi'],
  updateStatus: (docId: string, status: DocStatusEnum) => Promise<void>,
  setDocNeedsToBeUploaded: DocsTabContextType['setDocNeedsToBeUploaded'],
  docUploadModalApi: DocsTabContextType['docUploadModalApi'],
  rescanFraud: (attachmentId: string) => Promise<void>,
  fraudDetectionEnabled: boolean,
  redactionEnabled: boolean,
  disabled?: boolean,
): ColumnDef<Doc>[] => [
  {
    accessorKey: 'file',
    cell({ row }) {
      return row.original.file ? (
        <Flex>
          <Checkbox
            checked={selectedDocs.some((item) => item.id === row.original.id)}
            flexShrink={0}
            mr={4}
            onCheckedChange={(e) => onSelectChange(!!e.checked, row.original)}
            size="lg"
          />

          <Box>
            {row.original.displayName && (
              <Text>{row.original.displayName}</Text>
            )}
            <Text lineClamp={1} wordBreak="break-all">
              {row.original.file.displayAs || row.original.file.filename}
            </Text>
            {isRejected(row.original.status) && (
              <Flex
                alignItems="center"
                direction="row"
                justifyContent="flex-start"
              >
                {isRejectedEmailSent(row.original.sendRejectionEmail) && (
                  <Tooltip
                    content="Document rejection email was sent to customer"
                    positioning={{ placement: 'top' }}
                  >
                    <Box>
                      <Icon
                        as={CgMail}
                        color="var(--chakra-colors-primary-500)"
                      />
                    </Box>
                  </Tooltip>
                )}
                <Text>Rejection Reason: {row.original.rejectionReason}</Text>
              </Flex>
            )}
          </Box>
        </Flex>
      ) : (
        <Box>
          {row.original.displayName && <Text>{row.original.displayName}</Text>}
        </Box>
      );
    },
    enableSorting: false,
    header: 'Filename',
  },
  {
    accessorKey: 'status',
    cell({ row }) {
      return row.original.file ? (
        <Stack alignItems="center" direction="row" gap={2}>
          {getStatusIcon(row.original.status) && (
            <Icon
              as={getStatusIcon(row.original.status)}
              color={getStatusColour(row.original.status)}
            />
          )}

          <Text
            color={getStatusColour(row.original.status)}
            fontSize="xs"
            textTransform="uppercase"
          >
            {row.original.status}
          </Text>
        </Stack>
      ) : (
        <Text>REQUIRED</Text>
      );
    },
    header: 'Status',
  },
  ...(fraudDetectionEnabled
    ? [
        {
          accessorKey: 'file.fraudRisk',
          cell({ row }) {
            return (
              <FraudRiskPill
                file={row.original.file}
                fraudUpdates={fraudUpdates}
              />
            );
          },
          header: 'Fraud risk',
        } as ColumnDef<Doc>,
      ]
    : []),
  ...(redactionEnabled
    ? [
        {
          accessorKey: 'file.redactionStatus',
          cell({ row }) {
            return (
              <RedactionStatusPill
                file={row.original.file}
                fraudUpdates={fraudUpdates}
              />
            );
          },
          header: 'Redaction',
        } as ColumnDef<Doc>,
      ]
    : []),
  {
    accessorKey: 'uploadedAt',
    cell({ row }) {
      return row.original.uploadedAt ? (
        <Text>
          <DateTimeDisplay value={row.original.uploadedAt} />
        </Text>
      ) : null;
    },
    header: 'Upload date',
  },
  {
    accessorKey: 'type',
    cell({ row }) {
      return row.original.file ? (
        <Select
          key={row.original.id}
          borderColor="gray.300"
          defaultValue={row.original.type}
          disabled={
            !can(Permissions.DocsUpdate) ||
            row.original.status === DocStatusEnum.Checked
          }
          groups={docTypes}
          iconColor="gray.700"
          iconSize="1rem"
          name={`doc.${row.original.id}.type`}
          onChange={(e) =>
            updateType(row.original.id, e.target.value as string)
          }
          variant="outline"
        />
      ) : (
        <Text>{row.original.type}</Text>
      );
    },
    header: 'Type',
  },
  {
    accessorKey: 'id',
    cell({ row }) {
      return row.original.file ? (
        <Stack alignItems="center" direction="row" gap={2} justifyContent="end">
          <DownloadDisplay
            enabled={abortDownloadingAttachmentsFlag.enabled}
            fraudUpdates={fraudUpdates}
            row={row}
            uploadStatuses={uploadStatuses}
          />

          {canAny([Permissions.DocsUpdate, Permissions.DocsDelete]) && (
            <Menu.Root positioning={{ placement: 'bottom-end' }}>
              <Menu.Trigger asChild>
                <IconButton
                  aria-label="download"
                  colorPalette="gray"
                  lineHeight={0}
                  rounded="lg"
                  size="md"
                  variant="ghost"
                >
                  <CgMoreVerticalAlt />
                </IconButton>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content>
                    {can(Permissions.DocsDelete) && (
                      <Menu.Item
                        onClick={() => deleteDoc(row.original.id)}
                        value="delete"
                      >
                        Delete
                      </Menu.Item>
                    )}

                    {can(Permissions.DocsUpdate) &&
                      calculateMarkAsOptions(row.original.status).map(
                        (option, idx) => (
                          <Menu.Item
                            // eslint-disable-next-line react/no-array-index-key
                            key={idx}
                            onClick={() => {
                              if (option === 'rejected') {
                                setDocNeedsToBeRejected(row.original);
                                docRejectedModalApi.onOpen();
                              } else {
                                updateStatus(row.original.id, option);
                              }
                            }}
                            value={`mark-as-${option}`}
                          >
                            Mark as {option}
                          </Menu.Item>
                        ),
                      )}

                    {fraudDetectionEnabled && (
                      <FraudActionsMenuItems
                        file={row.original.file}
                        fraudUpdates={fraudUpdates}
                        rescanFraud={rescanFraud}
                      />
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          )}
        </Stack>
      ) : (
        <Stack alignItems="center" direction="row" gap={2}>
          {can(Permissions.DocsUpdate) && (
            <Button
              colorPalette="gray"
              disabled={disabled}
              fontWeight="normal"
              justifySelf="self-end"
              ml="auto"
              onClick={() => {
                setDocNeedsToBeUploaded(row.original);
                docUploadModalApi.onOpen();
              }}
              size="md"
              variant="outline"
            >
              <CgMathPlus />
              Add new
            </Button>
          )}

          {can(Permissions.DocsDelete) && (
            <IconButton
              aria-label="delete"
              colorPalette="red"
              ml={4}
              onClick={() => deleteDoc(row.original.id)}
              size="md"
            >
              <CgTrash />
            </IconButton>
          )}
        </Stack>
      );
    },
    enableSorting: false,
    header: '',
  },
];

const defaultSorting: Sorting = { column: 'updated_at', direction: 'desc' };

export const DocsTabTypeTable: FC<DocsTabTypeTableProps> = ({
  type,
  perPage,
  includes = EMPTY_ARR,
  documentTypeCategory = CategoryParent.DocumentType,
}) => {
  const { can, canAny } = useCan();
  const {
    setDocNeedsToBeUploaded,
    selectedDocs,
    setSelectedDocs,
    uuid,
    definition,
    docUploadModalApi,
    setDocNeedsToBeRejected,
    docRejectedModalApi,
    docTypes,
  } = useContext(DocsTabContext);

  const { deleteDoc: deleteApplicationDoc } = useDeleteApplicationDoc(
    uuid,
    definition,
  );
  const { updateDocument } = useUpdateApplicationDoc('doc', uuid);
  const { analyseAttachmentForFraud } =
    useAnalyseAttachmentForFraud(definition);
  const abortDownloadingAttachmentsFlag = useFeature(
    FeatureKey.AbortDownloadingAttachments,
  );
  const fraudDetectionFeature = useFeature(FeatureKey.FraudDetection);
  const documentRedactionFeature = useFeature(FeatureKey.DocumentRedaction);

  const [uploadStatuses, setUploadStatuses] =
    useState<UploadStatuses>(EMPTY_OBJ);
  const [fraudUpdates, setFraudUpdates] = useState<FraudAnalysisUpdate[]>([]);

  const additionalParams = useMemo(
    () =>
      createFilterParams({
        ...(type !== 'Uncategorized' ? { type } : undefined),
        document_type_category: documentTypeCategory,
      }),
    [type, documentTypeCategory],
  );

  const { tableProps, searchParams } = useTableParams(
    defaultSorting,
    perPage,
    additionalParams,
  );

  const isUncategorized = type === 'Uncategorized';

  const docsSearchParams = useMemo(
    () => ({
      ...(searchParams as Record<string, string>),
      ...buildIncludeParams(includes),
    }),
    [searchParams, includes],
  );

  const {
    docs,
    meta: docsMeta,
    ...docsQueryState
  } = useApplicationDocs(uuid, definition, docsSearchParams, {
    enabled: !isUncategorized,
    keepPreviousData: true,
    staleTime: 10000,
  });

  const {
    uncategorizedDocs,
    meta: uncategorizedMeta,
    ...uncategorizedQueryState
  } = useApplicationUncategorizedDocs(uuid, definition, docsSearchParams, {
    enabled: isUncategorized,
    keepPreviousData: true,
    staleTime: 10000,
  });

  const rawDocs = isUncategorized ? uncategorizedDocs : docs;
  const activeMeta = isUncategorized ? uncategorizedMeta : docsMeta;
  const activeQueryState = isUncategorized
    ? uncategorizedQueryState
    : docsQueryState;

  const transformedDocs = useMemo(
    () =>
      rawDocs
        ?.map(appendDisplayNameToDoc)
        .sort((doc) => (doc.file === undefined ? 1 : -1)) ?? EMPTY_ARR,
    [rawDocs],
  );

  const onSelectChange = useEventHandler((checked: boolean, doc: Doc): void => {
    setSelectedDocs((value) => {
      if (checked && !value.some((item) => item.id === doc.id)) {
        return [...value, doc];
      }

      if (!checked) {
        return value.filter((item) => item.id !== doc.id);
      }

      return value;
    });
  });

  const deleteDoc = useEventHandler(async (docId: string): Promise<void> => {
    try {
      await deleteApplicationDoc(docId);
    } catch {
      // Error handled in useDeleteApplicationDoc hook
    }
  });

  const updateStatus = useEventHandler(
    async (docId: string, status: DocStatusEnum): Promise<void> => {
      try {
        await updateDocument({ documentId: docId, data: { status } });
      } catch {
        // default error toast shown by request layer
      }
    },
  );

  const updateType = useEventHandler(
    async (docId: string, documentType: string): Promise<void> => {
      try {
        await updateDocument({
          documentId: docId,
          data: { type: documentType },
        });
      } catch {
        // default error toast shown by request layer
      }
    },
  );

  const rescanFraud = useEventHandler(
    async (attachmentId: string): Promise<void> => {
      try {
        await analyseAttachmentForFraud(attachmentId);
      } catch {
        // Error handled in useAnalyseAttachmentForFraud hook
      }
    },
  );

  useSubscribeToDocumentAttachmentUploadStateChanged(
    uuid,
    ({ attachmentId, uploadState }) => {
      setUploadStatuses((prevStatuses) => ({
        ...prevStatuses,
        [attachmentId]: uploadState,
      }));
    },
  );

  useSubscribeToFraudAnalysisUpdated(uuid, (update) => {
    setFraudUpdates((prevUpdates) => [...prevUpdates, update]);
  });

  const { hooks } = useContext(ApplicationContext);
  const disabled = isDeniedHook(hooks, 'docStore');

  const columns = useMemo(
    () =>
      getColumns(
        selectedDocs,
        onSelectChange,
        docTypes,
        can,
        canAny,
        updateType,
        abortDownloadingAttachmentsFlag,
        uploadStatuses,
        fraudUpdates,
        deleteDoc,
        setDocNeedsToBeRejected,
        docRejectedModalApi,
        updateStatus,
        setDocNeedsToBeUploaded,
        docUploadModalApi,
        rescanFraud,
        fraudDetectionFeature.enabled,
        documentRedactionFeature.enabled,
        disabled,
      ),
    [
      selectedDocs,
      docTypes,
      can,
      canAny,
      abortDownloadingAttachmentsFlag,
      uploadStatuses,
      fraudUpdates,
      setDocNeedsToBeRejected,
      docRejectedModalApi,
      setDocNeedsToBeUploaded,
      docUploadModalApi,
      fraudDetectionFeature.enabled,
      documentRedactionFeature.enabled,
    ],
  );

  const getRowProps = useEventHandler((row: Row<Doc>) => {
    if (!row.original.file) {
      return {
        style: {
          backgroundColor: '#FFFAF0',
        },
      };
    }
    return undefined;
  });

  return (
    <Box>
      <Heading as="h3" fontSize="18px" lineHeight="24px" mb={4}>
        {type === 'Id' ? type.toUpperCase() : type}
      </Heading>

      <ReactQueryTable<Doc>
        columns={columns}
        data={transformedDocs}
        getRowProps={getRowProps}
        isLoading={activeQueryState.isFetching}
        meta={activeMeta}
        {...tableProps}
      />
    </Box>
  );
};
